/**
 * Camada 1 · as duas superficies que TODA tela repete: o CARTAO e o AVISO.
 *
 * POR QUE ELAS NASCEM JUNTAS NO MESMO ARQUIVO: as duas sao a mesma decisao vista de dois
 * lados. Cartao e superficie NEUTRA que agrupa conteudo; aviso e superficie COLORIDA que
 * interrompe. Quem escreve uma tela escolhe entre os dois no mesmo instante, e te-los lado
 * a lado e o que impede a escolha de virar "copio o cartao e pinto de vermelho".
 *
 * O NUMERO QUE COMPROU O ARQUIVO, medido em 06/09/2026 nos apps da plataforma:
 *
 *   · `border-borda ... bg-superficie-elevada` .... 24 ocorrencias
 *   · `bg-erro-suave` e irmaos .................... 11 ocorrencias
 *
 * Sao 35 copias de duas decisoes. Para mudar como um cartao aparece nos dez produtos, hoje
 * seria preciso achar 24 lugares, e o vigesimo quinto nasceria na semana seguinte.
 *
 * E A COPIA NAO CUSTAVA SO A APARENCIA: nenhuma das 11 copias de aviso declarava `role`,
 * entao nenhuma delas era anunciada por leitor de tela. O aviso aparecia para quem ve e
 * simplesmente NAO EXISTIA para quem ouve, que e o mesmo defeito que este repositorio ja
 * registrou no contador de mensagens que nunca desenhava no telefone.
 */

import type { ReactNode } from 'react';
import { t } from '@complexity/i18n';

/* ===========================================================================
   O CARTAO
   =========================================================================== */

/* ---------------------------------------------------------------------------
   O QUE TODO CARTAO TEM, leve ele a algum lugar ou nao.
   --------------------------------------------------------------------------- */
interface CartaoComum {
  readonly children: ReactNode;
  /**
   * O titulo do cartao, quando ele tem um.
   *
   * OPCIONAL DE PROPOSITO: cartao que agrupa campos de um formulario ja esta debaixo do
   * titulo da tela, e repetir ali seria dizer duas vezes a mesma coisa.
   */
  readonly titulo?: string;
  /**
   * O cartao SEM respiro interno, para quem coloca uma lista ou tabela dentro dele.
   *
   * POR QUE ISTO E UMA PROP E NAO UMA CLASSE PASSADA DE FORA: lista dentro de cartao com
   * padding produz a margem dupla mais comum da plataforma, e a linha da lista deixa de
   * encostar na borda, que e onde o dedo a acerta no celular.
   */
  readonly semRespiro?: boolean;
}

/** O cartao de LEITURA: uma superficie que agrupa, e nada mais. */
interface CartaoDeLeitura extends CartaoComum {
  /** A acao no canto do cabecalho. So aparece quando ha titulo: sem ele, nao ha canto. */
  readonly acao?: ReactNode;
  readonly destino?: undefined;
  readonly aoNavegar?: undefined;
  readonly nomeAcessivel?: undefined;
}

/**
 * O cartao que LEVA A UM LUGAR.
 *
 * ===========================================================================
 * POR QUE ELE E LINK, E NAO BOTAO
 * ===========================================================================
 * Ele NAVEGA, e nao age. Um `<button>` aqui pareceria igual na tela e custaria tres coisas
 * que ninguem percebe faltando ate precisar delas: abrir em aba nova com cmd+click, o menu
 * de contexto com "copiar endereco", e o anuncio de "link" no leitor de tela, que e o que
 * avisa a pessoa de que ela vai SAIR desta tela.
 *
 * ===========================================================================
 * POR QUE ELE NAO IMPORTA UM ROTEADOR, E RECEBE `aoNavegar`
 * ===========================================================================
 * `packages/ui` e camada 1 e nao depende de `react-router`: um componente visual que
 * importa roteador amarra todo produto ao roteador do primeiro que precisou dele. Entao o
 * `href` e real, o clique comum e interceptado, e quem sabe navegar e o app.
 *
 * ===========================================================================
 * `nomeAcessivel` E OBRIGATORIO AQUI, E O TIPO E QUE GARANTE
 * ===========================================================================
 * Um cartao de metrica anuncia "5" e "Clientes ativos", e nenhum dos dois diz para onde o
 * clique leva. Quem ve descobre pelo cursor e pelo realce; quem ouve nao descobre de jeito
 * nenhum. Exigir o nome no TIPO e o que impede o cartao navegavel de nascer mudo, e e o
 * mesmo mecanismo do `aoFiltrar` do `CartaoMetrica`: nao ha como parecer clicavel sem ser,
 * e agora nao ha como levar sem dizer para onde.
 *
 * `acao` FICA DE FORA por exigencia do HTML, e nao por gosto: conteudo interativo dentro de
 * `<a>` e invalido, e um botao ali produz um alvo dentro do outro que o teclado nao
 * consegue separar.
 */
interface CartaoQueLeva extends CartaoComum {
  /** O endereco de destino. Vira `href`, entao precisa ser um caminho de verdade. */
  readonly destino: string;
  /**
   * Como o app navega. Ausente, o link faz carga completa da pagina, que continua correto
   * e custa o estado da tela.
   */
  readonly aoNavegar?: (destino: string) => void;
  /** O que o leitor de tela anuncia. Diz o numero, o que ele mede, e para onde leva. */
  readonly nomeAcessivel: string;
  readonly acao?: undefined;
}

export type CartaoProps = CartaoDeLeitura | CartaoQueLeva;

/**
 * As classes da superficie, escritas UMA VEZ e usadas pelos dois ramos.
 *
 * Separadas, o cartao que leva a um lugar acabaria com uma borda ou um raio ligeiramente
 * diferente do que so agrupa, e ninguem descobriria isso lendo o codigo.
 *
 * CANTOS CONCENTRICOS, o detalhe 1 do ADR 0052: o raio externo e `cx-cartao` e o respiro e
 * `p-cx-cartao`, os dois do mesmo par de tokens.
 */
const SUPERFICIE = 'overflow-hidden rounded-cx-cartao border border-borda bg-superficie-elevada';

export function Cartao(props: CartaoProps) {
  const { children, titulo, semRespiro } = props;

  // O miolo e o MESMO nos dois ramos: o que muda e a casca, e nao o conteudo.
  const miolo = (
    <>
      {titulo && (
        <header className="flex items-center gap-cx-3 px-cx-cartao pt-cx-cartao">
          <h2 className="min-w-0 flex-1 truncate text-cx-base font-semibold text-texto">
            {t(titulo)}
          </h2>
          {/* A acao so existe no cartao de leitura: o tipo ja proibiu a outra combinacao. */}
          {!props.destino && props.acao && <div className="shrink-0">{props.acao}</div>}
        </header>
      )}
      <div className={semRespiro ? (titulo ? 'mt-cx-4' : '') : 'p-cx-cartao'}>{children}</div>
    </>
  );

  if (!props.destino) return <section className={SUPERFICIE}>{miolo}</section>;

  const { destino, aoNavegar, nomeAcessivel } = props;

  return (
    <a
      href={destino}
      // O NOME ACESSIVEL SUBSTITUI o conteudo para quem ouve, e e isso que se quer: o
      // numero solto e o rotulo, lidos em sequencia, nao dizem que aquilo leva a algum
      // lugar. `aria-label` num link e a forma de dizer o destino sem escrever texto que
      // ocuparia espaco na tela para quem ve.
      aria-label={nomeAcessivel}
      onClick={(evento) => {
        // OS MODIFICADORES PASSAM DIRETO, e este ramo e o que preserva o cmd+click e o
        // botao do meio. Interceptar tudo faria a aba nova abrir a tela atual, que e o
        // defeito classico de quem envolve um link com `preventDefault` sem condicao.
        if (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.altKey) return;
        if (evento.button !== 0) return;
        if (!aoNavegar) return;
        evento.preventDefault();
        aoNavegar(destino);
      }}
      onKeyDown={(evento) => {
        // O ESPACO ACIONA, e isto e um DESVIO DECLARADO da convencao do navegador: num link
        // nativo o Espaco rola a pagina. Ele pediu os dois em 08/09/2026, e a extensao se
        // sustenta aqui porque o alvo e uma superficie inteira, e nao texto em linha: quem
        // chega nele pelo teclado tenta o Espaco como tentaria num botao. O Enter continua
        // sendo o do navegador, e nao e tratado aqui.
        if (evento.key !== ' ' && evento.key !== 'Spacebar') return;
        // `preventDefault` antes de tudo, porque sem ele a pagina rola JUNTO com a
        // navegacao, e a tela de destino chega rolada para baixo.
        evento.preventDefault();
        if (aoNavegar) aoNavegar(destino);
        else window.location.assign(destino);
      }}
      className={[
        SUPERFICIE,
        // `block` porque `<a>` nasce em linha, e em linha ele nao envolve o cartao inteiro:
        // o alvo ficaria do tamanho do texto, que e exatamente o defeito que este cartao
        // veio corrigir.
        'block cursor-pointer text-left transition-colors',
        // O REALCE DO PONTEIRO usa token, e nunca cor literal: um valor escrito a mao aqui
        // seria a unica cor da plataforma que nao muda quando a marca muda.
        'hover:bg-superficie-afundada',
        // O anel de foco NAO e declarado aqui: ele e global, no `:focus-visible` de
        // `styles.css`. Escrito de novo neste componente, ele ficaria diferente do resto no
        // dia em que um dos dois mudasse.
      ].join(' ')}
    >
      {miolo}
    </a>
  );
}

/* ===========================================================================
   O AVISO
   =========================================================================== */

/**
 * O TOM decide a cor E o papel de acessibilidade, e os dois juntos: separa-los deixaria
 * passar um aviso de erro anunciado como informacao, que e pior que nao anunciar.
 */
export type TomDeAviso = 'erro' | 'alerta' | 'sucesso' | 'informacao';

export interface AvisoProps {
  readonly tom: TomDeAviso;
  readonly children: ReactNode;
  /** O que fazer a respeito. Fica ao lado do texto, e nunca em outro canto da tela. */
  readonly acao?: ReactNode;
  /**
   * O titulo do aviso, quando o texto sozinho nao basta.
   *
   * OPCIONAL: a maioria dos avisos e uma frase, e um titulo em cima de uma frase e ruido.
   */
  readonly titulo?: string;
}

/**
 * O `role` de cada tom, e a diferenca entre eles NAO e estetica.
 *
 * `alert` INTERROMPE o leitor de tela e anuncia na hora; `status` espera a pessoa terminar
 * o que esta ouvindo. Erro e alerta interrompem porque a pessoa precisa saber ANTES de
 * continuar; sucesso e informacao esperam, porque interromper para dizer "salvo" e o tipo
 * de coisa que faz alguem desligar o leitor de tela.
 */
const PAPEL: Record<TomDeAviso, 'alert' | 'status'> = {
  erro: 'alert',
  alerta: 'alert',
  sucesso: 'status',
  informacao: 'status',
};

/**
 * As classes de cada tom. Fundo suave e texto na cor cheia: e o par que a plataforma ja
 * usa em selo, e mante-lo igual faz aviso e selo lerem como a mesma familia.
 */
const CORES: Record<TomDeAviso, string> = {
  /*
   * ============================================================================
   * O AVISO EM VIDRO, e este bloco alcanca a PLATAFORMA INTEIRA
   * ============================================================================
   * Pedidos dele em 09/09/2026, olhando o Financeiro nos dois temas:
   *   "ESSE LARANJA TB ESTA GRITANTE... REVEJA TUDO E COLOQUE TUDO SUAVE NA PLATAFORMA TODA."
   *   "TUDO GLASS E SUAVE. PADRAO PARA PLT"
   *
   * O QUE ESTAVA ERRADO: `bg-alerta-suave` e uma cor OPACA, e no tema escuro ela vale
   * `120 53 15` (tokens.css), um marrom-laranja cheio. O aviso pintava a faixa inteira de
   * ponta a ponta dentro do cartao, e num cartao pequeno isso deixa de ser um recado e passa
   * a ser o fundo. A palavra "suave" no NOME do token enganava: ela descreve a posicao na
   * escala, e nao o material.
   *
   * VIDRO E TINTA COM ALPHA BAIXO sobre o que esta atras, com desfoque e saturacao. O padrao
   * da casa proibe "gradiente saturado como fundo de bloco" com todas as letras: o material
   * e vidro sobre conteudo, e nao cor sobre cor.
   *
   * CADA TEMA TEM O SEU ALPHA, e isso foi APRENDIDO na tela hoje, nao deduzido: eu apliquei
   * um valor unico primeiro, e ele reprovou. Tinta clara sobre fundo quase preto ganha
   * contraste; tinta escura sobre fundo quase branco perde. A mesma distancia NUMERICA rende
   * menos no claro, entao o claro leva mais tinta e mais borda.
   *
   * A BORDA ENTROU, e ela nao existia: sem fundo cheio, a faixa precisa de contorno para ser
   * uma faixa. Vidro sem borda definida vira uma mancha, e no claro ela quase desaparece.
   *
   * O TEXTO CONTINUA NA COR CHEIA DO ESTADO (`text-erro` e irmaos), e nao ganhou alpha: ele
   * e o portador da informacao, e o unico elemento aqui que PRECISA de contraste maximo.
   */
  erro: 'border border-erro/40 bg-erro/15 text-erro dark:border-erro/25 dark:bg-erro/10 backdrop-blur-vidro backdrop-saturate-vidro',
  alerta:
    'border border-alerta/40 bg-alerta/15 text-alerta dark:border-alerta/25 dark:bg-alerta/10 backdrop-blur-vidro backdrop-saturate-vidro',
  sucesso:
    'border border-sucesso/40 bg-sucesso/15 text-sucesso dark:border-sucesso/25 dark:bg-sucesso/10 backdrop-blur-vidro backdrop-saturate-vidro',
  // `text-marca-forte` pelo mesmo motivo do botao primario: `text-marca` sobre esta tinta
  // deu 4.32:1 na medicao de 09/09/2026, abaixo do piso.
  informacao:
    'border border-marca/40 bg-marca/15 text-marca-forte dark:border-marca/25 dark:bg-marca/10 backdrop-blur-vidro backdrop-saturate-vidro',
};

/** O simbolo de cada tom. COR SOZINHA NAO EXISTE para quem nao a distingue. */
const SIMBOLO: Record<TomDeAviso, string> = {
  erro: '!',
  alerta: '!',
  sucesso: '✓',
  informacao: 'i',
};

export function Aviso({ tom, children, acao, titulo }: AvisoProps) {
  return (
    <div
      role={PAPEL[tom]}
      className={`flex items-start gap-cx-3 rounded-cx-cartao-interno p-cx-3 text-cx-sm ${CORES[tom]}`}
    >
      {/* O simbolo e `aria-hidden` porque o `role` ja carrega a urgencia para quem ouve:
          anuncia-lo seria dizer "exclamacao" antes da frase, toda vez. */}
      <span
        aria-hidden="true"
        className="mt-[1px] flex h-5 w-5 shrink-0 items-center justify-center rounded-cx-full border border-current text-cx-xs font-bold"
      >
        {SIMBOLO[tom]}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-cx-1">
        {titulo && <p className="font-semibold">{t(titulo)}</p>}
        <div className="min-w-0">{children}</div>
      </div>
      {acao && <div className="shrink-0">{acao}</div>}
    </div>
  );
}
